// Bordered card with a numbered header bar (index badge + title + optional
// mono id chip + remove button) used by the module and schedule editors.
// Extracted because both editors rendered a line-for-line identical header
// bar; the fields body is passed as children.

import { type ReactNode } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "../common";

interface NumberedItemCardProps {
  /** Zero-based position; the badge displays index + 1. */
  index: number;
  /** Title text — callers pass `name || 'New X'`. */
  title: string;
  /** Optional identifier rendered as a mono chip next to the title. */
  identifier?: string;
  onRemove: () => void;
  removeAriaLabel: string;
  children: ReactNode;
}

export default function NumberedItemCard({
  index,
  title,
  identifier,
  onRemove,
  removeAriaLabel,
  children,
}: NumberedItemCardProps) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="bg-slate-50 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 text-caption font-semibold">
            {index + 1}
          </span>
          <span className="text-body-sm font-semibold text-slate-800 dark:text-slate-200">
            {title}
          </span>
          {identifier && (
            <span className="text-caption text-slate-400 font-mono">
              {identifier}
            </span>
          )}
        </div>
        <Button
          variant="danger"
          iconOnly
          size="sm"
          icon={<Trash2 className="h-4 w-4" />}
          aria-label={removeAriaLabel}
          className="btn-icon-compact"
          type="button"
          onClick={onRemove}
        />
      </div>
      <div className="p-4 space-y-4">{children}</div>
    </div>
  );
}
